<template>
  <BaseSimpleModal
    class="first-sale-achievement-modal"
    width="420px"
    has-close-button
    @close="handleClose"
  >
    <template #body>
      <div class="achievement-art">
        <img
          class="achievement-art__image"
          :src="artUrl"
          alt="Conquista Primeira Venda: Minha primeira venda com o Prata! Eu vendo com o Prata Digital. #EuVendoComOPrata"
        >
      </div>
    </template>

    <template #actions>
      <button
        class="button achievement-actions__share"
        :disabled="busy"
        @click="onShare"
      >
        Compartilhar conquista
      </button>
      <button
        class="button achievement-actions__download"
        :disabled="busy"
        @click="onDownload()"
      >
        Baixar imagem
      </button>
    </template>
  </BaseSimpleModal>
</template>

<script>
// useGtm() é só um getter da instância singleton do plugin (ver
// node_modules/@gtm-support/vue2-gtm) — funciona igual dentro de métodos
// do Options API, não exige setup()/Composition API de verdade.
import { useGtm } from '@gtm-support/vue2-gtm';
import BaseSimpleModal from '@/components/BaseSimpleModal.vue';
import { recordEvent } from '@/services/v1/achievements';
import trackGtmEvent from '@/utils/trackGtmEvent';
import ACHIEVEMENT_EVENTS from '@/utils/achievementEvents';

// Arte final aprovada (card B2B-320, anexo "Story Primeira Venda-selection") —
// estática, sem personalização por vendedor. Servida de public/ para poder
// ser baixada/compartilhada como arquivo, sem depender de captura de DOM.
const ART_URL = '/img/achievements/first-sale-share.jpg';
const ART_FILENAME = 'minha-primeira-venda-prata.jpg';
const SHARE_TITLE = 'Minha primeira venda com o Prata Digital';
const SHARE_TEXT = 'Primeira proposta desembolsada. Eu vendo com o Prata Digital! #EuVendoComOPrata';

// Botão Baixar e fallback do Compartilhar (navegador sem Web Share) gravam o
// mesmo first_sale_image_downloaded; sem a origem a métrica junta os dois.
const DOWNLOAD_SOURCE_BUTTON = 'download_button';
const DOWNLOAD_SOURCE_SHARE_FALLBACK = 'share_fallback';

/**
 * B2B-320 — modal de celebração da primeira venda, aberto a partir do clique
 * na notificação "🏆 Primeira venda" (ver notificationActionBus/Layout.vue).
 */
export default {
  name: 'FirstSaleAchievementModal',
  components: { BaseSimpleModal },
  props: {
    achievementId: {
      type: [Number, String],
      required: true,
    },
  },
  data: () => ({
    busy: false,
    hasInteractedWithCta: false,
    artUrl: ART_URL,
  }),
  mounted() {
    // RN02: primeiro clique que abre o modal marca a conquista como vista —
    // idempotente no backend, reabertura manual nunca é bloqueada.
    this.safeRecordEvent(ACHIEVEMENT_EVENTS.FIRST_SALE.MODAL_VIEWED);
  },
  methods: {
    handleClose() {
      // "Fechado sem interação com CTAs" (evento do card) — só dispara se o
      // usuário não baixou nem tentou compartilhar antes de fechar.
      if (!this.hasInteractedWithCta) {
        this.safeRecordEvent(ACHIEVEMENT_EVENTS.FIRST_SALE.CLOSED);
      }
      this.$emit('close');
    },
    async fetchArtAsFile() {
      const response = await fetch(this.artUrl);
      if (!response.ok) {
        // Sem isso, um 404/500 vira um "arquivo" com o corpo do erro —
        // share/download de algo corrompido sem nenhum aviso visível.
        throw new Error(`Falha ao buscar a arte (HTTP ${response.status})`);
      }
      const blob = await response.blob();
      return new File([blob], ART_FILENAME, { type: blob.type || 'image/jpeg' });
    },
    // Payload vem do backend (data.analytics), nunca remontado aqui — e só
    // depois do 200, pra o GA4 contar o que o banco tem.
    async safeRecordEvent(eventName, eventSource = null) {
      try {
        const response = await recordEvent(this.achievementId, eventName, eventSource);
        trackGtmEvent(useGtm(), eventName, response?.data?.data?.analytics);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[achievements] falha ao registrar evento', eventName, e);
      }
    },
    // RN05: só a arte, sem overlay/botões/menus — já garantido por ser um
    // arquivo estático (não uma captura de DOM).
    async onDownload(eventSource = DOWNLOAD_SOURCE_BUTTON) {
      this.hasInteractedWithCta = true;
      this.safeRecordEvent(ACHIEVEMENT_EVENTS.FIRST_SALE.IMAGE_DOWNLOADED, eventSource);

      const link = document.createElement('a');
      link.href = this.artUrl;
      link.download = ART_FILENAME;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    // RN06: recurso nativo de compartilhamento quando disponível; fallback
    // para download quando não suportado (CA08). Cancelar o share nativo
    // não conta como "sem suporte" — não cai no fallback.
    async onShare() {
      this.hasInteractedWithCta = true;
      this.safeRecordEvent(ACHIEVEMENT_EVENTS.FIRST_SALE.SHARE_CLICKED);

      this.busy = true;
      try {
        const file = await this.fetchArtAsFile();
        const shareData = { files: [file], title: SHARE_TITLE, text: SHARE_TEXT };

        if (navigator.canShare && navigator.canShare(shareData)) {
          await navigator.share(shareData);
          this.safeRecordEvent(ACHIEVEMENT_EVENTS.FIRST_SALE.SHARE_COMPLETED);
          return;
        }
      } catch (e) {
        if (e && e.name === 'AbortError') return; // usuário cancelou o share nativo
      } finally {
        this.busy = false;
      }

      this.onDownload(DOWNLOAD_SOURCE_SHARE_FALLBACK);
    },
  },
};
</script>

<style lang="scss" scoped>
// Cor amostrada diretamente da arte aprovada (cantos do PNG original) —
// pra transição entre a imagem e o rodapé do modal ficar imperceptível.
$achievement-navy: #1a1c31;
// Verde amostrado direto do botão "Baixar imagem" do layout aprovado.
$achievement-mint: #05f8ce;

.first-sale-achievement-modal {
  // Essa classe cai direto no elemento raiz do BaseSimpleModal (.modal),
  // via fallthrough do Vue — por isso o z-index vai aqui, escopado só a
  // este modal, e não no componente compartilhado (revisão de PR: subir
  // o z-index do .modal genérico afetaria todo modal do admin).
  z-index: 999 !important;

  // BaseSimpleModal é branco/genérico por padrão (usado em vários outros
  // modais do admin) — aqui sobrescrevemos só esta instância pra virar uma
  // superfície escura contínua, igual ao layout aprovado no card.
  // !important de propósito: BaseSimpleModal é compartilhado por outros
  // modais do admin, então a ordem de carregamento do CSS entre eles não é
  // garantida — isso evita depender de quem carrega por último.
  ::v-deep {
    .modal-content {
      position: relative !important;
      background: $achievement-navy !important;
      padding: 0 !important;
      border-radius: 16px !important;
      overflow: hidden !important;
    }

    .modal-content__header {
      position: absolute !important;
      inset: 0 0 auto 0 !important;
      min-height: 0 !important;
      padding: 14px !important;
      z-index: 2 !important;
      pointer-events: none; // clique passa pra arte por baixo, exceto no botão
    }

    .close-button {
      position: static !important;
      pointer-events: auto;
      margin-left: auto;
      width: 28px;
      height: 28px;
      display: flex !important;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.16) !important;
      color: #fff !important;
      font-size: 13px;
    }

    .modal-content__body {
      padding: 0 !important;
      min-height: 0 !important;
    }

    .modal-content__actions {
      padding: 0 20px 20px !important;
      gap: 12px !important;
    }
  }
}

.achievement-art {
  display: flex;
  overflow: hidden;

  &__image {
    // Sem recorte: um corte fixo em altura cortava o texto da arte no meio
    // (ex: "O PRATA!" pela metade) em telas diferentes. Mostra a arte
    // inteira, largura 100% e altura proporcional — o download/compartilhar
    // já entregava (e continua entregando) o arquivo original completo.
    width: 100%;
    height: auto;
    display: block;
  }
}

.achievement-actions__share,
.achievement-actions__download {
  flex: 1;
  // min-width:0 é o que falta pro Bulma deixar o botão encolher abaixo do
  // texto "Compartilhar conquista" — sem isso, o item de flex nunca fica
  // menor que o conteúdo, o que empurra o modal pra fora da tela no mobile
  // (nowrap padrão do .button do Bulma).
  min-width: 0;
  min-height: 44px;
  padding: 8px 10px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  line-height: 1.2;
  white-space: normal !important;
  border: 1px solid transparent;
}

.achievement-actions__share {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.5);
  color: #fff;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.08);
  }
}

.achievement-actions__download {
  background: $achievement-mint;
  border-color: $achievement-mint;
  color: $achievement-navy;

  &:hover:not(:disabled) {
    background: darken($achievement-mint, 6%);
  }
}
</style>
